import { useTranslation } from 'react-i18next';
import Popover from 'antd/es/popover';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { IconRcAccountCommentIcon } from '@/assets';
import { IconAge } from '@/assets/overview';
import { IconRcUser } from '@/assets/whales';
import { useCurrentAddressInfo, usePrivate } from '@/hooks';
import { openUserDealModal, UserCardPopupPropsPartial, UserCardPopup } from '@/components';
import {
  getTimeFromNow,
  shortenAddress,
  login,
  formatNum,
  isSybilByUserDesc,
  openNewWindow,
} from '@/utils';

import HelperTooltip from '../HelperTooltip';
import MemoEditModal from '../UserTag/MemoEditModal';
import { UserAvatar } from '../UserAvatar';
import './index.scss';
import { useMuteOperate } from '@/views/Stream/components/FeedMoreAction/hooks';
import { setAllUserSybil } from '../UserDeal/SpamReport/hooks';
import { SuicideTag } from '@/views/Profile/components/SuicideTag';

export const MemoTag = ({
  memo,
  address,
  onSuccess,
  isShowMemoTooltip = false,
  hideBorder,
  supportAdd,
  className,
}: {
  memo: string;
  address: string;
  onSuccess?(v: string): void;
  isShowMemoTooltip?: boolean;
  hideBorder?: boolean;
  supportAdd?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const [isPrivate] = usePrivate();
  const [addr, setAddr] = useState<string>('');
  const { address: current, isVerified } = useCurrentAddressInfo();

  if (current === address || !current || !isVerified) {
    return null;
  }

  const Memo = isPrivate ? null : (
    <div
      title={memo}
      className={clsx(
        className,
        'db-user-tag is-memo',
        !supportAdd && !memo && 'is-empty',
        hideBorder && 'hide-border',
      )}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();

        if (!address || !isVerified) {
          login();
          return;
        }

        setAddr(address);
      }}>
      <IconRcAccountCommentIcon className="db-user-tag-icon"></IconRcAccountCommentIcon>
      {memo ? <span className="db-user-tag-memo">{memo}</span> : null}
    </div>
  );

  return (
    <>
      {isShowMemoTooltip && !memo ? (
        <HelperTooltip inParent={false} title={t('MemoTag.addTip')}>
          {Memo}
        </HelperTooltip>
      ) : (
        Memo
      )}
      {addr && (
        <MemoEditModal
          addr={addr}
          prevMemo={memo}
          success={onSuccess}
          onClose={() => {
            setAddr('');
          }}></MemoEditModal>
      )}
    </>
  );
};

export const UserTags = ({
  data,
  children,
  onMemoChange,
  isShowAge,
  onRefetch,
  isShowMemoTooltip,
  isShowSybilTooltip,
  disableMemo,
  hideMuteTag,
}: Pick<UserCardProps, 'data' | 'onMemoChange'> & {
  children?: ReactNode;
  isShowAge?: boolean;
  onRefetch?: () => void;
  isShowMemoTooltip?: boolean;
  isShowSybilTooltip?: boolean;
  disableMemo?: boolean;
  hideMuteTag?: boolean;
}) => {
  const { t } = useTranslation();

  const { address: currentUserAddress } = useCurrentAddressInfo();
  const openSpamModal = useCallback(() => {
    if (!data?.id) {
      return;
    }
    openUserDealModal({
      address: data?.id,
      refetchCallback: () => setAllUserSybil({ currentId: currentUserAddress!, userId: data?.id }),
    });
  }, [currentUserAddress, data?.id]);
  const { address: myAddress } = useCurrentAddressInfo();
  const { mutate: unmuteUser, data: unMuteRes } = useMuteOperate({
    action: 'unmute',
    userId: data?.id!,
  });

  const isCex = useMemo(
    () =>
      data?.cex?.id &&
      (data.cex.is_deposit ||
        data.cex.is_vault ||
        data.cex.is_withdraw ||
        data.cex.is_collect ||
        data.cex.is_gastopup ||
        data.cex.is_deposit),
    [data?.cex],
  );

  const spamTooltip = useMemo(() => {
    return myAddress === data?.id ? (
      <>
        {t('UserCard.i6mimv')}{' '}
        <span className={'db-user-tag-tooltip-link-text'} onClick={openSpamModal}>
          {t('UserCard.5vkmuc')}
        </span>{' '}
        {t('UserCard.1niy615')}
      </>
    ) : (
      <>
        {t('UserCard.ehrrk3')}{' '}
        <span className={'db-user-tag-tooltip-link-text'} onClick={openSpamModal}>
          {t('UserCard.vn1jki')}
        </span>
      </>
    );
  }, [data?.id, myAddress, openSpamModal, t]);
  const muteTooltip = useMemo(
    () => (
      <>
        {t('UserCard.rek43k')}{' '}
        <span className={'db-user-tag-tooltip-link-text'} onClick={() => unmuteUser()}>
          {t('UserCard.15zbbyq')}
        </span>
      </>
    ),
    [unmuteUser, t],
  );
  const Name = data?.name ? (
    <div className="db-user-tag is-name" title={data?.name}>
      <span className="db-user-tag-content">{data?.name} </span>
    </div>
  ) : null;

  const Cex = isCex ? (
    data?.cex?.is_deposit ? (
      <Popover content={'Deposit address'} overlayClassName="db-user-popover">
        <div className="db-user-tag is-cex" title={data.cex?.name}>
          <span className="db-user-tag-content">{data.cex?.name} </span>
          <IconRcUser className="db-user-tag-icon"></IconRcUser>
        </div>
      </Popover>
    ) : (
      <div className="db-user-tag is-cex" title={data!.cex?.name}>
        <span className="db-user-tag-content">{data!.cex?.name}</span>
      </div>
    )
  ) : null;

  const protocol = Object.values(data?.protocol || {}).find(item => item?.name);
  const Protocol = protocol?.name ? (
    <div className="db-user-tag" title={protocol?.name}>
      <span className="db-user-tag-content">
        {t('UserCard.n8apoi')}
        {protocol?.name}
      </span>
    </div>
  ) : null;

  let _contract = {
    name: '',
  };
  Object.values(data?.contract || {}).forEach(chain => {
    if (chain.multisig) {
      _contract = {
        name: chain.multisig.name ? `MultiSig: ${chain.multisig.name}` : 'MultiSig',
      };
      return;
    }
    if (_contract.name) {
      return;
    }

    Object.values(chain || {}).forEach(item => {
      if (_contract.name) {
        return;
      }
      _contract.name = item?.name;
    });
  });

  const Contract = _contract.name ? (
    <div className="db-user-tag" title={_contract.name}>
      <span className="db-user-tag-content">{_contract.name}</span>
    </div>
  ) : null;

  const Sybil = isSybilByUserDesc(data) ? (
    isShowSybilTooltip ? (
      <HelperTooltip title={spamTooltip}>
        <div className="db-user-tag">{t('UserCard.1bu9xb')}</div>
      </HelperTooltip>
    ) : (
      <div className="db-user-tag">{t('UserCard.1bu9xb')}</div>
    )
  ) : null;

  const Mute =
    data?.user?.is_muted && !unMuteRes?.is_success ? (
      <HelperTooltip title={muteTooltip}>
        <div className="db-user-tag">{t('UserCard.18hbnv')}</div>
      </HelperTooltip>
    ) : null;

  const Age =
    isShowAge && data?.born_at ? (
      <HelperTooltip
        title={t('SocialTag_SocialTag.age', {
          age: dayjs.unix(data?.born_at).format('YYYY-MM-DD'),
        })}>
        <div className="db-user-tag is-age">
          <img src={IconAge} alt="" className="db-user-tag-icon" />
          {getTimeFromNow(data?.born_at)}
        </div>
      </HelperTooltip>
    ) : null;

  return (
    <div className="db-user-tag-list">
      {/* {Tags} */}
      {Name}
      {Protocol}
      {Contract}
      {Cex}
      {!hideMuteTag ? Mute : null}
      {Sybil}
      {Age}
      {children}
      {!disableMemo && (
        <MemoTag
          memo={data?.user.memo || ''}
          address={data?.id!}
          onSuccess={onMemoChange}
          isShowMemoTooltip={isShowMemoTooltip}
        />
      )}
    </div>
  );
};

export const UserName = (props: {
  address?: string;
  name?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  userDesc?: UserDesc;
}) => {
  const { address, name: web3Id, onClick, className, userDesc } = props;
  const isSuicide = useMemo(() => userDesc?.user?.is_suicide, [userDesc?.user?.is_suicide]);
  const data = useMemo(() => {
    if (web3Id) {
      return {
        name: web3Id,
        className: 'is-web3',
      };
    }
    if (address) {
      return {
        name: shortenAddress(address),
        className: 'is-address',
      };
    }
    return {
      name: '-',
      className: 'is-empty',
    };
  }, [web3Id, address]);
  return (
    <div
      className={clsx('db-user-name', data.className, className, onClick && 'is-clickable')}
      title={web3Id || address}
      onClick={onClick}>
      {isSuicide ? <SuicideTag userAddress={userDesc?.id} /> : data.name}
    </div>
  );
};

interface UserCardProps {
  extra?: ReactNode;
  onMemoChange?(v: string): void;
  data?: UserDesc;
  isShowAmount?: boolean;
  enableClickToOpenProfile?: boolean;
  relaction?: string;
  children?: ReactNode;
  disableMemo?: boolean;
  userCardPopup?: UserCardPopupPropsPartial;
  hideMuteTag?: boolean;
}

export const UserCard = (props: UserCardProps) => {
  const {
    data,
    isShowAmount,
    onMemoChange,
    extra,
    userCardPopup = true,
    relaction,
    enableClickToOpenProfile,
    children,
    disableMemo,
    hideMuteTag,
  } = props;
  const openProfile = useCallback(() => {
    if (!enableClickToOpenProfile) {
      return;
    }
    openNewWindow(`/profile/${data?.id}`, '_blank');
  }, [data?.id, enableClickToOpenProfile]);

  return (
    <div className="db-user">
      <UserAvatar
        data={data}
        userCardPopup={userCardPopup}
        onClick={enableClickToOpenProfile ? openProfile : undefined}
        officialLogoOption={{ size: 14 }}
        groupLogoOption={{ size: 14 }}
      />
      <div className="db-user-content">
        <div className="db-user-row">
          <UserCardPopup
            {...(userCardPopup === true ? {} : userCardPopup || {})}
            address={data?.id || ''}>
            <UserName
              name={data?.user?.web3_id}
              address={data?.id}
              userDesc={data}
              onClick={enableClickToOpenProfile ? openProfile : undefined}></UserName>
          </UserCardPopup>
          {isShowAmount ? (
            <div className="db-user-amount">${formatNum(data?.usd_value, 0)}</div>
          ) : null}
          {extra}
        </div>
        <UserTags
          data={data}
          onMemoChange={onMemoChange}
          disableMemo={disableMemo}
          hideMuteTag={hideMuteTag}>
          {relaction ? <div className="db-user-tag is-relaction">{relaction}</div> : null}
        </UserTags>
        {children}
      </div>
    </div>
  );
};

type Props = { avatarSize?: number; nameClassName?: string; className?: string } & Pick<
  UserCardProps,
  'data' | 'userCardPopup' | 'enableClickToOpenProfile'
>;

export const UserAvatarWithName = (props: Props) => {
  const {
    avatarSize = 24,
    data,
    userCardPopup = true,
    enableClickToOpenProfile = true,
    nameClassName,
    className,
  } = props;
  const popupOptions = useMemo(
    () => (userCardPopup === true ? {} : userCardPopup || {}),
    [userCardPopup],
  );

  const openProfile = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      if (!enableClickToOpenProfile) {
        return;
      }
      openNewWindow(`/profile/${data?.id}`, '_blank');
    },
    [data?.id, enableClickToOpenProfile],
  );

  return (
    <div className={clsx('db-user', className)}>
      <UserAvatar
        data={data}
        userCardPopup={popupOptions}
        onClick={enableClickToOpenProfile ? openProfile : undefined}
        size={avatarSize}
      />
      <div className="db-user-content">
        <UserCardPopup {...popupOptions} address={data?.id || ''}>
          <UserName
            name={data?.user?.web3_id}
            address={data?.id}
            className={nameClassName}
            userDesc={data}
            onClick={enableClickToOpenProfile ? openProfile : undefined}
          />
        </UserCardPopup>
      </div>
    </div>
  );
};
